import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { AnimSet } from '../anim/atlas.ts';
import { skinned } from '../anim/skin.ts';

/** Minimal model contract (see models/index.ts). */
export interface ModelLike {
  root: THREE.Group;
  tick(n: number): void;
  height: number;
}

/** One merged mesh per material. */
export interface BakedPart {
  geometry: THREE.BufferGeometry;
  material: THREE.Material;
  /** The model's own material it came from, and how it turned into `material` (see partMaterial). */
  from?: PartSource;
}

export interface PartSource {
  mat: THREE.Material;
  shared: boolean;
  skin: boolean;
}

/** A glow sprite captured at bake time (rendered as instanced billboards). */
export interface BakedGlow {
  pos: THREE.Vector3;
  color: THREE.Color;
  size: number;
  opacity: number;
  /** Rigged models: the bone it rides (0 for the model's own group). */
  bone: number;
}

export interface BakedFrame {
  parts: BakedPart[];
  glows: BakedGlow[];
}

/** A sub-assembly that the game animates on its own (tower turret, catapult arm…). */
export interface BakedDynamic {
  name: string;
  /** Pivot transform relative to the model root. */
  base: THREE.Matrix4;
  frame: BakedFrame;
}

export interface BakedModel {
  key: string;
  frames: BakedFrame[];
  dynamic: BakedDynamic[];
  height: number;
  triangles: number;
  /** Rigged models: their clips (frames carry `aBone` and skinned materials). */
  anim: AnimSet | null;
}

const KEEP_ATTRS = ['position', 'normal', 'uv', 'color', 'finish', 'aBone'] as const;

/**
 * Plain painted materials (one flat colour, at most the shared bump noise, opaque, no shader tweaks) only differ by their
 * colour: baking that colour into the vertices lets every part with the same finish share one material,
 * so a model draws in a handful of calls instead of one per colour. It looks exactly the same.
 */
const SHARED = new Map<string, THREE.Material>();
const plainCompile = THREE.Material.prototype.onBeforeCompile;
const paintable = (m: THREE.Material): m is THREE.MeshStandardMaterial | THREE.MeshBasicMaterial => {
  const s = m as THREE.MeshStandardMaterial;
  if (!(s.isMeshStandardMaterial || (m as THREE.MeshBasicMaterial).isMeshBasicMaterial)) return false;
  if ((m as THREE.MeshPhysicalMaterial).isMeshPhysicalMaterial) return false;
  if (m.transparent || m.opacity < 1 || m.alphaTest > 0 || m.vertexColors || m.onBeforeCompile !== plainCompile || m.userData.keep) return false;
  if (s.map || s.alphaMap || s.aoMap || s.lightMap || s.envMap) return false;
  if (s.isMeshStandardMaterial && (s.emissiveMap || s.normalMap || s.roughnessMap || s.metalnessMap || s.displacementMap)) return false;
  // Self-lit materials brighten at night on their own (materials.ts): they keep their own material.
  if (m.userData.base !== undefined) return false;
  return true;
};
const finishKey = (m: THREE.MeshStandardMaterial | THREE.MeshBasicMaterial): string => {
  const base: (string | number | boolean)[] = [m.type, m.side, m.depthWrite, m.depthTest, m.toneMapped, m.fog, m.wireframe, m.polygonOffset, m.polygonOffsetFactor, m.polygonOffsetUnits, m.blending];
  if ((m as THREE.MeshStandardMaterial).isMeshStandardMaterial) {
    const s = m as THREE.MeshStandardMaterial;
    // Roughness, metalness and bump strength ride in the vertices too (see PER_VERTEX): only what the
    // shader can't take per vertex splits the parts.
    base.push(s.emissive.getHexString(), s.emissiveIntensity.toFixed(3), s.flatShading, s.envMapIntensity, s.bumpMap?.uuid ?? '-');
  }
  return base.join('|');
};

/**
 * Per-vertex finish for shared standard materials: roughness, metalness and bump strength come from the
 * `finish` attribute instead of uniforms, so stone, wood, metal and cloth of a model draw together.
 */
const PER_VERTEX = (shader: THREE.WebGLProgramParametersWithUniforms): void => {
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', '#include <common>\nattribute vec3 finish;\nvarying vec3 vFinish;')
    .replace('#include <begin_vertex>', '#include <begin_vertex>\nvFinish = finish;');
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <common>', '#include <common>\nvarying vec3 vFinish;')
    .replace('#include <roughnessmap_fragment>', 'float roughnessFactor = vFinish.x;')
    .replace('#include <metalnessmap_fragment>', 'float metalnessFactor = vFinish.y;')
    .replace('#include <bumpmap_pars_fragment>', THREE.ShaderChunk.bumpmap_pars_fragment.replace('uniform float bumpScale;', '').replace(/bumpScale/g, 'vFinish.z'));
};

const sharedFor = (m: THREE.MeshStandardMaterial | THREE.MeshBasicMaterial): THREE.Material => {
  const k = finishKey(m);
  let out = SHARED.get(k);
  if (!out) {
    const c = m.clone();
    (c as THREE.MeshStandardMaterial).color.set(1, 1, 1);
    c.vertexColors = true;
    c.name = `shared:${k}`;
    if ((c as THREE.MeshStandardMaterial).isMeshStandardMaterial) {
      c.onBeforeCompile = PER_VERTEX;
      c.customProgramCacheKey = () => 'somnhold-per-vertex-finish';
    }
    out = c;
    SHARED.set(k, out);
  }
  return out;
};

/** What a still model's piece shares a draw by when its material is plain paint (its finish, the colour going in the vertices); null otherwise. */
export const paintKey = (m: THREE.Material): string | null => (paintable(m) ? `shared:${finishKey(m)}` : null);

/** A plain-paint piece in `frame` (from the mesh to the merged mesh's space), painted, and the material it then shares (paintKey first). */
export const paintedPiece = (mesh: THREE.Mesh, frame: THREE.Matrix4): { geo: THREE.BufferGeometry; material: THREE.Material } => {
  const m = mesh.material as THREE.MeshStandardMaterial | THREE.MeshBasicMaterial;
  const geo = normalizeGeometry(mesh.geometry, frame, -1);
  paint(geo, m);
  return { geo, material: sharedFor(m) };
};

/** The material a part drawn with the model's `src` takes: shared by finish (painted in the vertices), skinned. */
export const partMaterial = (src: THREE.Material, shared: boolean, skin: boolean): THREE.Material => {
  const m = shared ? sharedFor(src as THREE.MeshStandardMaterial) : src;
  return skin ? skinned(m) : m;
};

/** Paints a geometry with a material's colour. */
const paint = (geo: THREE.BufferGeometry, m: THREE.MeshStandardMaterial | THREE.MeshBasicMaterial): void => {
  const n = geo.getAttribute('position').count;
  const arr = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    arr[i * 3] = m.color.r;
    arr[i * 3 + 1] = m.color.g;
    arr[i * 3 + 2] = m.color.b;
  }
  geo.setAttribute('color', new THREE.Float32BufferAttribute(arr, 3));
  const st = m as THREE.MeshStandardMaterial;
  if (st.isMeshStandardMaterial) {
    const f = new Float32Array(n * 3);
    const bump = st.bumpMap ? st.bumpScale : 0;
    for (let i = 0; i < n; i++) {
      f[i * 3] = st.roughness;
      f[i * 3 + 1] = st.metalness;
      f[i * 3 + 2] = bump;
    }
    geo.setAttribute('finish', new THREE.Float32BufferAttribute(f, 3));
  }
};

const normalizeGeometry = (g: THREE.BufferGeometry, m: THREE.Matrix4, bone: number): THREE.BufferGeometry => {
  const geo = g.index ? g.toNonIndexed() : g.clone();
  geo.applyMatrix4(m);
  if (bone >= 0) geo.setAttribute('aBone', new THREE.BufferAttribute(new Uint8Array(geo.getAttribute('position').count).fill(bone), 1));
  if (!geo.getAttribute('normal')) geo.computeVertexNormals();
  if (!geo.getAttribute('uv')) {
    const n = geo.getAttribute('position').count;
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array(n * 2), 2));
  }
  for (const name of Object.keys(geo.attributes)) {
    if (!(KEEP_ATTRS as readonly string[]).includes(name)) geo.deleteAttribute(name);
  }
  geo.morphAttributes = {};
  geo.clearGroups();
  return geo;
};

/**
 * Merges every mesh under `root` (excluding subtrees in `skip`) into one geometry per material, in `root`'s space.
 * With `bones`, each vertex records the nearest bone above its mesh and the parts take skinned materials.
 */
const snapshot = (root: THREE.Object3D, skip: Set<THREE.Object3D>, rootInv: THREE.Matrix4, bones?: Map<THREE.Object3D, number>): BakedFrame => {
  root.updateMatrixWorld(true);
  const byMat = new Map<THREE.Material, THREE.BufferGeometry[]>();
  const from = new Map<THREE.Material, PartSource>();
  const glows: BakedGlow[] = [];
  const rel = new THREE.Matrix4();
  const visit = (o: THREE.Object3D, parentBone: number): void => {
    if (skip.has(o) || !o.visible) return;
    const bone = bones ? (bones.get(o) ?? parentBone) : -1;
    if ((o as THREE.Sprite).isSprite) {
      const sp = o as THREE.Sprite;
      const mat = sp.material as THREE.SpriteMaterial;
      const p = new THREE.Vector3().setFromMatrixPosition(sp.matrixWorld).applyMatrix4(rootInv);
      const sc = new THREE.Vector3().setFromMatrixScale(sp.matrixWorld);
      glows.push({ pos: p, color: mat.color.clone(), size: sc.x, opacity: mat.opacity, bone: Math.max(0, bone) });
    } else if ((o as THREE.Mesh).isMesh || (o as THREE.LineSegments).isLineSegments) {
      const mesh = o as THREE.Mesh;
      if ((mesh as unknown as THREE.LineSegments).isLineSegments) return;
      const mat = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
      if (!mat) return;
      rel.multiplyMatrices(rootInv, mesh.matrixWorld);
      const geo = normalizeGeometry(mesh.geometry, rel, bone);
      const shared = paintable(mat);
      if (shared) paint(geo, mat);
      else if (geo.getAttribute('color')) geo.deleteAttribute('color');
      const into = partMaterial(mat, shared, !!bones);
      if (!from.has(into)) from.set(into, { mat, shared, skin: !!bones });
      const list = byMat.get(into) ?? [];
      list.push(geo);
      byMat.set(into, list);
    }
    for (const c of o.children) visit(c, bone);
  };
  visit(root, 0);
  const parts: BakedPart[] = [];
  // Opaque first, then transparent: stable draw order.
  const mats = [...byMat.keys()].sort((a, b) => Number(a.transparent) - Number(b.transparent));
  for (const mat of mats) {
    const geos = byMat.get(mat) as THREE.BufferGeometry[];
    const merged = geos.length === 1 ? (geos[0] as THREE.BufferGeometry) : mergeGeometries(geos, false);
    if (!merged) continue;
    merged.computeBoundingSphere();
    parts.push({ geometry: merged, material: mat, from: from.get(mat) as PartSource });
    for (const g of geos) if (g !== merged) g.dispose();
  }
  return { parts, glows };
};

export interface BakeOptions {
  /** Called before each frame snapshot (tick the model, set a pose…). */
  frames: ((m: ModelLike) => void)[];
  /** Names (userData.part) of sub-assemblies baked separately for runtime animation. */
  dynamic?: string[];
  /** Rigged: the bones in index order (bake them at rest) and their sampled clips. */
  skin?: { bones: THREE.Object3D[]; anim: AnimSet };
}

export const bakeModel = (key: string, model: ModelLike, opts: BakeOptions): BakedModel => {
  const dynNodes: { name: string; node: THREE.Object3D }[] = [];
  if (opts.dynamic && opts.dynamic.length > 0) {
    model.root.traverse((o) => {
      const p = o.userData.part as string | undefined;
      if (p && opts.dynamic?.includes(p)) dynNodes.push({ name: p, node: o });
    });
  }
  const skip = new Set(dynNodes.map((d) => d.node));
  model.root.updateMatrixWorld(true);
  const rootInv = new THREE.Matrix4().copy(model.root.matrixWorld).invert();
  const boneIx = opts.skin ? new Map(opts.skin.bones.map((b, i) => [b, i] as const)) : undefined;
  const frames: BakedFrame[] = [];
  for (const f of opts.frames) {
    f(model);
    frames.push(snapshot(model.root, skip, rootInv, boneIx));
  }
  const dynamic: BakedDynamic[] = dynNodes.map(({ name, node }) => {
    node.updateMatrixWorld(true);
    const base = new THREE.Matrix4().multiplyMatrices(rootInv, node.matrixWorld);
    const inv = new THREE.Matrix4().copy(node.matrixWorld).invert();
    return { name, base, frame: snapshot(node, new Set(), inv) };
  });
  let triangles = 0;
  for (const fr of frames.slice(0, 1)) for (const p of fr.parts) triangles += p.geometry.getAttribute('position').count / 3;
  for (const d of dynamic) for (const p of d.frame.parts) triangles += p.geometry.getAttribute('position').count / 3;
  return { key, frames, dynamic, height: model.height, triangles, anim: opts.skin?.anim ?? null };
};
